/**
 * @file CreatableSelectWhiteDraggable.tsx
 * @module app/custom/Select/CreatableSelectWhiteDraggable
 * @description Renders the CreatableSelectWhiteDraggable reusable select control, including option presentation, creation, multi-selection, and table-filter behavior.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import { useState, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const CreatableSelectWhiteDraggable = (props: any) => {
  const [selectedValues, setSelectedValues] = useState(props.value || []);

  useEffect(() => {
    setSelectedValues(props.value || []);
  }, [props.value]);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const updatedValues = [...selectedValues];
    const [movedItem] = updatedValues.splice(result.source.index, 1);
    updatedValues.splice(result.destination.index, 0, movedItem);

    setSelectedValues(updatedValues);
    props.onChange?.(updatedValues);
  };

  const handleCreate = (inputValue: string) => {
    if (!inputValue.trim()) return;

    const newItem = { title: inputValue, label: inputValue, value: inputValue };

    setSelectedValues((prev: any) => {
      const updatedValues = [...prev, newItem];
      props.onChange?.(updatedValues);
      return updatedValues;
    });
  };

  const handleRemove = (removedItem: any) => {
    setSelectedValues((prev: any) => {
      const updatedValues = prev.filter((item: any) => item.value !== removedItem.value);
      props.onChange?.(updatedValues, { action: "remove-value", removedValue: removedItem });
      return updatedValues;
    });
  };

  const MultiValue = (props: any) => {
    const index = selectedValues.findIndex((item: any) => item.value === props.data.value);

    return (
      <Draggable draggableId={props.data.value} index={index}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className="cursor-grab d-flex gap-6"
          >
            <div className="d-flex align-items-center gap-2" style={{ padding: '8px 14px', margin: '2px', backgroundColor: "#e7f1fd", boxSizing: 'border-box', borderRadius: '6px' }}>
              {props.children}
              <i className="bi bi-x" style={{ fontSize: "20px", color: "black", cursor: "pointer" }} onClick={() => handleRemove(props.data)}></i>
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className="p-4 border rounded-md w-full">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="selected-options" direction="horizontal">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <CreatableSelect
                isLoading={props.isLoading}
                isClearable={props.isClearable}
                options={props.options}
                defaultValue={props.defaultValue}
                styles={{
                  option: (base: any) => ({
                    ...base,
                    border: `1px solid #f9f9f9`,
                    margin: "0px",
                    background: "white",
                    padding: "1rem 1.25rem",
                    ":active": { ...base[":active"], color: "#1b74e4", background: "#f1faff" },
                    ":hover": { ...base[":active"], color: "#1b74e4", background: "#f1faff" },
                    color: "#5e6278",
                    fontSize: "16px",
                    fontWeight: "600",
                  }),
                  control: (base: any) => ({
                    ...base,
                    background: "#ffff",
                    boxShadow: "none",
                    minHeight: "40px",
                    height: "auto",
                    border: props.border ? "0.5px solid " + props.border : "0px",
                    ":hover": { ...base[":active"], border: props.border ? "0.5px solid " + props.border : "0px" },
                    borderRadius: "8px",
                    padding: "6.5px 20.4px 6.5px 10px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }),
                  multiValue: (base: any) => ({
                    ...base,
                    fontSize: "16px",
                    fontWeight: "600",
                    borderRadius: "6px",
                    backgroundColor: "#e7f1fd",
                    padding: "8px 14px",
                    color: "#1a1a1a",
                  }),
                  indicatorsContainer: (base: any) => ({
                    ...base,
                    color: "#f9f9f9",
                    strokeWidth: "5px",
                    display: props.display ? props.display : base.display,
                  }),
                  multiValueRemove: (base: any) => ({
                    ...base,
                    cursor: "pointer",
                    color: "#1a1a1a",
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "center",
                    svg: { width: "16px", height: "16px", color: "#1a1a1a" },
                    ":hover": { backgroundColor: "#dbeafe", color: "#1b74e4" },
                  }),
                }}
                value={selectedValues}
                onCreateOption={handleCreate}
                onChange={(newValue: any, actionMeta: any) => {
                  setSelectedValues(newValue);
                  props.onChange?.(newValue, actionMeta);
                }}
                placeholder={props.placeholder}
                isMulti={props.isMulti}
                components={{ MultiValue }}
                getOptionLabel={props.getOptionLabel}
                menuIsOpen={props.menuIsOpen}
                onKeyDown={props.onKeyDown}
                inputValue={props.inputValue}
                onInputChange={props.onInputChange}
                onBlur={props.onBlur}
              />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export { CreatableSelectWhiteDraggable };
